import { capitalizeString } from '@order-management/format';
import { RegistrationFilePermissions } from '@order-management/roles';
import {
  InputType,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  RegistrationFilesCriteria,
  RegistrationFilesTableFilterDataType,
  RegistrationFilesTableRow,
  RegistrationFileStatus,
  SortDirection,
} from '@order-management/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Columns, Element, Icon } from 'react-bulma-components';
import { FaCircleInfo, FaPlus } from 'react-icons/fa6';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSearchParams,
} from 'react-router-dom';
import { environment } from '../../../environments/environment';
import BackButton from '../../shared/back-button/back-button';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import Conditional from '../../shared/conditional/conditional';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import { useCSVDownload } from '../../shared/hooks/csv-download';
import useHttpRequest from '../../shared/hooks/http-request';
import useToast from '../../shared/hooks/toasts';
import ProtectedPage from '../../shared/protected-page/protected-page';
import {
  createSearchParams,
  getSearchParamValue,
  getSearchParamValues,
} from '../../shared/url';
import BoxLayout from '../../ui/box-layout/box-layout';
import Button from '../../ui/button/button';
import Link from '../../ui/link/link';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { OnTableChangeData } from '../../ui/table/types';
import CreateRegistrationFileModal from '../create-registration-file-modal/create-registration-file-modal';
import {
  createRegistrationFile,
  downloadRegistrationFile,
  fetchRegistrationFiles,
  sendRegistrationFile,
} from '../registration-files-service';
import RegistrationFilesTable from '../registration-files-table/registration-files-table';
import SendRegistrationFileModal from '../send-registration-file-modal/send-registration-file-modal';

const CREATE_REGISTRATION_FILE_ERROR_MESSAGE =
  'The registration file could not be created. Please try again';
const DOWNLOAD_REGISTRATION_FILE_ERROR_MESSAGE =
  'The registration file could not be downloaded. Please try again';
const SEND_REGISTRATION_FILE_ERROR_MESSAGE =
  'The registration file could not be sent. Please try again';
const SEND_REGISTRATION_FILE_SUCCESS_MESSAGE =
  'Registration file sent successfully';

const initialCriteria: RegistrationFilesCriteria = {
  itemsPerPage: 50,
  pageNumber: 1,
  search: '',
  sortBy: '',
  sortDirection: SortDirection.ASC,
};

const resolveCriteria = (searchParams?: URLSearchParams) => {
  return {
    itemsPerPage: parseInt(
      getSearchParamValue(searchParams, 'itemsPerPage') ?? '50'
    ),
    pageNumber: parseInt(
      getSearchParamValue(searchParams, 'pageNumber') ?? '1'
    ),
    search: getSearchParamValue(searchParams, 'search') ?? '',
    sortBy: getSearchParamValue(searchParams, 'sortBy') ?? 'name',
    sortDirection:
      getSearchParamValue<SortDirection>(searchParams, 'sortDirection') ??
      SortDirection.DES,
    name: getSearchParamValues(searchParams, 'name'),
    status: getSearchParamValues<RegistrationFileStatus>(
      searchParams,
      'status'
    ),
  };
};

export const loader: LoaderFunction = async ({ request }) => {
  const searchParams = createSearchParams(request);

  const response = fetchRegistrationFiles(resolveCriteria(searchParams));

  return defer({ response });
};

export function RegistrationFilesPageContent() {
  const response =
    useAsyncValue() as PaginatedAPIResponse<RegistrationFilesTableRow>;
  const navigation = useNavigation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const [filterCriteria, setFilterCriteria] =
    useState<RegistrationFilesCriteria>(resolveCriteria(searchParams));
  const [showCreateRegistrationFileModal, setShowCreateRegistrationFileModal] =
    useState<boolean>(false);
  const {
    executeHttpRequest: executeFetchRegistrationFileData,
    isLoading: isLoadingFetchRegistrationFileData,
  } = useHttpRequest();
  const {
    executeHttpRequest: executeSendregistrationFile,
    isLoading: isLoadingSendRegistrationFile,
  } = useHttpRequest();
  const {
    executeHttpRequest: executeCreateNewRegistrationFile,
    isLoading: isLoadingCreateNewRegistrationFile,
  } = useHttpRequest();
  const {
    executeHttpRequest: executeDownloadRegistrationFile,
    isLoading: isLoadingDownloadRegistrationFile,
  } = useHttpRequest();

  const [showSendRegistrationFileModal, setShowSendRegistrationFileModal] =
    useState<boolean>(false);
  const [selectedRegistrationFileId, setSelectedRegistrationFileId] =
    useState<string>('');

  const [tableData, setTableData] = useState<RegistrationFilesTableRow[]>(
    response.data
  );
  const [tableConfig, setTableConfig] = useState<PaginatedContentConfig>({
    ...filterCriteria,
    pages: response.pages,
  });

  const { downloadCSV } = useCSVDownload();

  const filtersData = useMemo(() => {
    return {
      name: {
        title: 'Registration file name',
        appliedCount: filterCriteria.name?.length,
        value: filterCriteria.name,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'name',
          valueKey: 'name',
          placeholder: 'Search by Registration file name',
        },
      },
      status: {
        title: 'Status',
        appliedCount: filterCriteria.status?.length,
        value: filterCriteria.status,
        input: {
          type: InputType.SELECT,
          valueKey: 'status',
          options: Object.values(RegistrationFileStatus).map((status) => ({
            label: capitalizeString(status),
            value: status,
          })),
        },
      },
    };
  }, [
    filterCriteria.name,
    filterCriteria.status,
  ]) as FiltersData<RegistrationFilesCriteria>;

  const appliedFilters = useMemo(
    () =>
      Object.entries(filtersData).filter(([_, filter]) => filter.appliedCount),
    [filtersData]
  );

  const emptyContent = useMemo(() => {
    const emptyContentText =
      appliedFilters.length > 0 || tableConfig.search !== ''
        ? 'There are no registration files that match the selected criteria'
        : 'No registration files have been created yet';
    return (
      <Element
        display="flex"
        justifyContent="center"
        className="my-2 has-text-weight-light"
      >
        <span>{emptyContentText}</span>
      </Element>
    );
  }, [appliedFilters.length, tableConfig.search]);

  const isDataLoading = useMemo(
    () => navigation.state === 'loading' || isLoadingFetchRegistrationFileData,
    [isLoadingFetchRegistrationFileData, navigation.state]
  );

  const fetchRegistrationFilesData = useCallback(
    (criteria: RegistrationFilesCriteria) => {
      executeFetchRegistrationFileData({
        asyncFunction: async () => {
          const response = await fetchRegistrationFiles(criteria);
          setTableData(response.data);
          setTableConfig({ ...criteria, pages: response.pages });
        },
      });
    },
    [executeFetchRegistrationFileData]
  );

  const handleOnTableChange = useCallback(
    (value: OnTableChangeData<RegistrationFilesTableRow>): void => {
      const newData = {
        ...filterCriteria,
        ...value.config,
        sortBy: value.column?.key || filterCriteria.sortBy,
      };
      setFilterCriteria(newData);
      fetchRegistrationFilesData(newData);
    },
    [fetchRegistrationFilesData, filterCriteria]
  );

  const handleTableFilterSubmit = useCallback(
    async (value: RegistrationFilesTableFilterDataType) => {
      const { itemsPerPage, search, sortBy, sortDirection } = filterCriteria;
      const newCriteria: RegistrationFilesCriteria = {
        ...value,
        itemsPerPage,
        search,
        sortBy,
        sortDirection,
        pageNumber: 1,
      };
      setFilterCriteria(newCriteria);
      fetchRegistrationFilesData(newCriteria);
    },
    [fetchRegistrationFilesData, filterCriteria]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof RegistrationFilesCriteria)[]) => {
      const newCriteria = { ...filterCriteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newCriteria[filterDataKey];
        } else {
          newCriteria[filterDataKey] = initialFilterValue as string &
            string[] &
            number;
        }
      }
      handleTableFilterSubmit(newCriteria);
    },
    [filterCriteria, handleTableFilterSubmit]
  );

  const handleCreateNewRegistrationFile = useCallback(async () => {
    await executeCreateNewRegistrationFile({
      asyncFunction: async () => {
        const response = await createRegistrationFile();
        setSelectedRegistrationFileId(response.id);
        setShowCreateRegistrationFileModal(true);
      },
      customErrorMessage: CREATE_REGISTRATION_FILE_ERROR_MESSAGE,
    });
    fetchRegistrationFilesData(filterCriteria);
  }, [
    executeCreateNewRegistrationFile,
    fetchRegistrationFilesData,
    filterCriteria,
  ]);

  const handleDownloadRegistrationFileClick = useCallback(
    async (id: string) => {
      await executeDownloadRegistrationFile({
        asyncFunction: async () => {
          const response = await downloadRegistrationFile(id);
          downloadCSV(response);
        },
        customErrorMessage: DOWNLOAD_REGISTRATION_FILE_ERROR_MESSAGE,
      });

      function onDownload() {
        setShowCreateRegistrationFileModal(false);
        window.removeEventListener('focus', onDownload);
        fetchRegistrationFilesData(filterCriteria);
      }
      window.addEventListener('focus', onDownload);
    },
    [
      downloadCSV,
      executeDownloadRegistrationFile,
      fetchRegistrationFilesData,
      filterCriteria,
    ]
  );

  const handleDisplaySendRegistrationFileClick = useCallback(
    async (fileId: string) => {
      setSelectedRegistrationFileId(fileId);
      setShowSendRegistrationFileModal(true);
    },
    []
  );

  const handleSendRegistrationFile = useCallback(
    async (recipients: string[]) => {
      await executeSendregistrationFile({
        asyncFunction: async () => {
          await sendRegistrationFile(selectedRegistrationFileId, recipients);
          setShowSendRegistrationFileModal(false);
          toast.success(SEND_REGISTRATION_FILE_SUCCESS_MESSAGE);
        },
        customErrorMessage: SEND_REGISTRATION_FILE_ERROR_MESSAGE,
      });
    },
    [executeSendregistrationFile, selectedRegistrationFileId, toast]
  );

  const handleCloseSendRegistrationFileModal = useCallback(() => {
    setShowSendRegistrationFileModal(false);
  }, []);

  const handleCloseRegistrationFileModal = useCallback(() => {
    setShowCreateRegistrationFileModal(false);
  }, []);

  const navigateToMyDevices = useCallback(() => {
    navigate('/my-devices');
  }, [navigate]);

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 my-4"
      header={
        <Element className="p-4">
          <BackButton onClick={navigateToMyDevices} />
          <Columns marginless>
            <Columns.Column className="pl-0">
              <h1 className="is-size-3 has-text-weight-light">
                Device registration
              </h1>
              <h3 className="is-size-5 has-text-weight-light">
                Create and email a registration file which zSpace devices
                already in the field can use to enroll into the current system
              </h3>
            </Columns.Column>
            <CheckPermissions
              permissions={
                RegistrationFilePermissions.REGISTRATION_FILES_CREATE
              }
            >
              <CheckPermissions.Render>
                <Columns.Column
                  display="flex"
                  justifyContent="flex-end"
                  className="gap-3 is-narrow"
                >
                  <Button
                    color="primary-dark"
                    onClick={handleCreateNewRegistrationFile}
                    isExecutingAction={isLoadingCreateNewRegistrationFile}
                  >
                    <Button.LoadingIcon icon={FaPlus} />
                    <span>Create new registration file</span>
                  </Button>
                </Columns.Column>
              </CheckPermissions.Render>
            </CheckPermissions>
          </Columns>
        </Element>
      }
    >
      <Conditional condition={isDataLoading}>
        <Conditional.True>
          <Element display="flex" justifyContent="center">
            <PageSpinner />
          </Element>
        </Conditional.True>
        <Conditional.False>
          <Element display="flex" className="w-full" alignItems="center" mb={2}>
            <FilterBar
              initialData={initialCriteria}
              data={filterCriteria}
              filtersData={filtersData}
              onFilterSubmit={handleTableFilterSubmit}
              onRemoveFilter={onRemoveFilters}
            />
          </Element>
          <RegistrationFilesTable
            dataSource={tableData}
            config={tableConfig}
            onChange={handleOnTableChange}
            empty={emptyContent}
            handleDownloadRegistrationFileClick={
              handleDownloadRegistrationFileClick
            }
            isDownloading={isLoadingDownloadRegistrationFile}
            handleSendRegistrationFileClick={
              handleDisplaySendRegistrationFileClick
            }
            form={{
              enableSearch: false,
            }}
          />
          <p>
            <Icon className="has-text-primary-dark is-justify-content-start">
              <FaCircleInfo />
            </Icon>
            <span>
              Alternatively, devices can be registered manually on site without
              a registration file.&nbsp;
            </span>
            <Link
              color="primary"
              underlined
              to={environment.supportUrl}
              openNewTab
            >
              Learn more
            </Link>
          </p>
        </Conditional.False>
      </Conditional>
      <CreateRegistrationFileModal
        show={showCreateRegistrationFileModal}
        id={selectedRegistrationFileId}
        onDownloadFileClick={handleDownloadRegistrationFileClick}
        onSendEmailClick={() => {
          handleDisplaySendRegistrationFileClick(selectedRegistrationFileId);
          setShowCreateRegistrationFileModal(false);
        }}
        onCloseClick={handleCloseRegistrationFileModal}
        isExecutingAction={isLoadingDownloadRegistrationFile}
      />
      <SendRegistrationFileModal
        show={showSendRegistrationFileModal}
        onCancel={handleCloseSendRegistrationFileModal}
        isSendingFile={isLoadingSendRegistrationFile}
        handleSendRegistrationFile={handleSendRegistrationFile}
      />
    </BoxLayout>
  );
}

export function RegistrationFilesPage() {
  const { response } = useLoaderData() as {
    response: Promise<PaginatedAPIResponse<RegistrationFilesTableRow>>;
  };

  return (
    <ProtectedPage
      permissions={RegistrationFilePermissions.REGISTRATION_FILES_VIEW}
    >
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <RegistrationFilesPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default RegistrationFilesPage;
